import { Container } from 'hypo-container';
import { observable } from 'mobx';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../constants/PaymentProviders';

export interface IPaymentSourceDefinition {
  paymentProviderName: PAYMENT_PROVIDERS;
  methods: PAYMENT_MODES[];
  displayOrder?: number;
}

export interface IPaymentMethodSetupConfig {
  definition: IPaymentSourceDefinition;
  diContainer: Container;
}

export type PaymentMethodSetup = (config: IPaymentMethodSetupConfig) => Promise<void>;


export interface IPaymentMethodsSetupMap {
  [id: string]: PaymentMethodSetup;
}

/**
 * Map for payment methods setup.
 */
const paymentMethodsSetup: IPaymentMethodsSetupMap = observable({}) as unknown as IPaymentMethodsSetupMap;

/**
 * Map for payment methods UI setup.
 */
const paymentMethodsUISetup: IPaymentMethodsSetupMap = {};

// dynamically load payment by Provider
const setupPaymentMethods = (): void => {
  // adyen web
  paymentMethodsSetup[PAYMENT_PROVIDERS.ADYEN_WEB] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodAdyenWebSetup' */ '../../payment-strategies/adyen-web/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.ADYEN_WEB] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodAdyenWebSetup-UI' */ '../../payment-strategies/adyen-web/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // radial alipay
  paymentMethodsSetup[PAYMENT_PROVIDERS.RADIAL_ALIPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodRadialAlipaySetup' */ '../../payment-strategies/radial-alipay/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.RADIAL_ALIPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodRadialAlipaySetup-UI' */ '../../payment-strategies/radial-alipay/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // radial credit card
  paymentMethodsSetup[PAYMENT_PROVIDERS.RADIAL] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodRadialCCSetup' */ '../../payment-strategies/radial/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.RADIAL] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodRadialCCSetup-UI' */ '../../payment-strategies/radial/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // iyzico payment
  paymentMethodsSetup[PAYMENT_PROVIDERS.IYZICO] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodIyzicoSetup' */ '../../payment-strategies/iyzico/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.IYZICO] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodIyzicoSetup-UI' */ '../../payment-strategies/iyzico/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // afterpay payment
  paymentMethodsSetup[PAYMENT_PROVIDERS.AFTERPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodAfterpaySetup' */ '../../payment-strategies/afterpay/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.AFTERPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodAfterpaySetup-UI' */ '../../payment-strategies/afterpay/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // GSI PayPal
  paymentMethodsSetup[PAYMENT_PROVIDERS.GSI_PAYPAL] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGSIPayPalSetup' */ '../../payment-strategies/gsi-paypal/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.GSI_PAYPAL] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGSIPayPalSetup-UI' */ '../../payment-strategies/gsi-paypal/setupUI'
    );
    await setupModule.setupUI(config);
  };

  //PayPal
  paymentMethodsSetup[PAYMENT_PROVIDERS.PAYPAL] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGSIPayPalSetup' */ '../../payment-strategies/paypal/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.PAYPAL] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGSIPayPalSetup-UI' */ '../../payment-strategies/paypal/setupUI'
    );
    await setupModule.setupUI(config);
  };

  // Ingenico
  paymentMethodsSetup[PAYMENT_PROVIDERS.INGENICO] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodIngenicoSetup' */ '../../payment-strategies/ingenico/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.INGENICO] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodIngenicoSetup-UI' */ '../../payment-strategies/ingenico/setupUI'
    );
    await setupModule.setupUI(config);
  };
  
  // GlobalCollect
  paymentMethodsSetup[PAYMENT_PROVIDERS.GLOBAL_COLLECT] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGlobalCollectSetup' */ '../../payment-strategies/global-collect/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.GLOBAL_COLLECT] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGlobalCollectSetup-UI' */ '../../payment-strategies/global-collect/setupUI'
    );
    await setupModule.setupUI(config);
  };

  // PayU
  paymentMethodsSetup[PAYMENT_PROVIDERS.PAYU] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodPayuSetup' */ '../../payment-strategies/payu/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.PAYU] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodPayuSetup-UI' */ '../../payment-strategies/payu/setupUI'
    );
    await setupModule.setupUI(config);
  };
  // Mercado Pago
  paymentMethodsSetup[PAYMENT_PROVIDERS.MERCADO_PAGO] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodMercadoPagoSetup' */ '../../payment-strategies/mercado-pago/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.MERCADO_PAGO] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodMercadoPagoSetup-UI' */ '../../payment-strategies/mercado-pago/setupUI'
    );
    await setupModule.setupUI(config);
  };

  // Braintree
  paymentMethodsSetup[PAYMENT_PROVIDERS.BRAINTREE] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodBraintreeSetup' */ '../../payment-strategies/braintree/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.BRAINTREE] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodBraintreeSetup-UI' */ '../../payment-strategies/braintree/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // GMO Rakuten
  paymentMethodsSetup[PAYMENT_PROVIDERS.GMORPay] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGMORakutenSetup' */ '../../payment-strategies/gmo-rakuten/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.GMORPay] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGMORakutenSetup-UI' */ '../../payment-strategies/gmo-rakuten/setupUI'
    );
    await setupModule.setupUI(config);
  };

  // Cash On Delivery
  paymentMethodsSetup[PAYMENT_PROVIDERS.COD] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodCODSetup' */ '../../payment-strategies/cash-on-delivery/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.COD] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodCODSetup-UI' */ '../../payment-strategies/cash-on-delivery/setupUI'
    );
    await setupModule.setupUI(config);
  };

  // Beleza Boleto
  paymentMethodsSetup[PAYMENT_PROVIDERS.BELEZA] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodBelezaSetup' */ '../../payment-strategies/beleza/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.BELEZA] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodBelezaSetup-UI' */ '../../payment-strategies/beleza/setupUI'
    );
    await setupModule.setupUI(config);
  };

  // mockpay payment
  paymentMethodsSetup[PAYMENT_PROVIDERS.MOCKPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodMockpaySetup' */ '../../payment-strategies/mockpay/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.MOCKPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodMockpaySetup-UI' */ '../../payment-strategies/mockpay/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // gift card payment
  paymentMethodsSetup[PAYMENT_PROVIDERS.GIFT_CARD] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodGiftCardSetup' */ '../../payment-strategies/gift-card/setup'
    );
    await setupModule.setup(config);
  };

  // TCTP payment
  paymentMethodsSetup[PAYMENT_PROVIDERS.TCTP] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(/* webpackChunkName: 'PaymentMethodTCTPSetup' */ '../../payment-strategies/tctp/setup');
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.TCTP] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(/* webpackChunkName: 'PaymentMethodTCTPSetup-UI' */ '../../payment-strategies/tctp/setupUI');
    await setupModule.setupUI(config);
  };

  paymentMethodsUISetup[PAYMENT_PROVIDERS.GIFT_CARD] = async () => {
    /* eslint-disable-next-line @typescript-eslint/no-unused-expressions */
    () => {};
  };
  // kueski payment
  paymentMethodsSetup[PAYMENT_PROVIDERS.KUESKI] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodKueskiSetup' */ '../../payment-strategies/kueski/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.KUESKI] = async config => {
    // eslint-disable-next-line max-len
    const setupUIModule = await import(
      /* webpackChunkName: 'PaymentMethodKueskiSetup-UI' */ '../../payment-strategies/kueski/setupUI'
    );
    await setupUIModule.setupUI(config);
  };

  // AltaPay
  paymentMethodsSetup[PAYMENT_PROVIDERS.ALTAPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodAltaPaySetup' */ '../../payment-strategies/altapay/setup'
    );
    await setupModule.setup(config);
  };
  paymentMethodsUISetup[PAYMENT_PROVIDERS.ALTAPAY] = async config => {
    // eslint-disable-next-line max-len
    const setupModule = await import(
      /* webpackChunkName: 'PaymentMethodAltaPaySetup-UI' */ '../../payment-strategies/altapay/setupUI'
    );
    await setupModule.setupUI(config);
  };
};

export { paymentMethodsSetup, paymentMethodsUISetup, setupPaymentMethods };
